/**
 * useCampaignShare — canonical share helpers for V2 campaign pages.
 *
 * SINGLE SOURCE OF TRUTH for share-option construction, device gating, and
 * robust share-link opening. Both `V2ShareModal.vue` and `V2SupportOnboarding.vue`
 * (and the EditCampaign QR/share panel) should consume these helpers rather
 * than re-deriving their own (drifted) option arrays.
 *
 * These are plain functions with NO Vue reactivity so Options-API components
 * can import and call them directly from computed/methods.
 *
 * Behavior baked in (per client Rick's requests):
 *   - Share buttons reliably open (popup with a same-tab fallback when blocked).
 *   - The SMS / "Text" button is HIDDEN on desktop (no native Messages app).
 *   - Instagram OPENS Instagram (Rick 2026-06-11: "Instagram button does not
 *     open Instagram. It just copies the link"). Instagram still has no web
 *     share-intent that accepts a URL, so the button copies the link first
 *     (ready to paste into a story/DM) and then opens instagram.com in a new
 *     tab — on mobile the universal link hands off to the installed app.
 */

/**
 * Inline SVG glyphs for each share button, keyed by option `key`.
 *
 * This module is now the CANONICAL source for these share-icon SVGs —
 * V2SupportOnboarding.vue imports them from here (its old local `ICONS`
 * const was removed in favor of this module). Provenance: facebook /
 * twitter / linkedin / email / sms / instagram originated in the v2
 * components (Lucide-derived, 18x18, stroke/fill mix); whatsapp + copy
 * originated in EditCampaign.vue's `SOCIAL_ICONS` (16x16, solid fill).
 * Keeping the real markup here is what eliminates the visual drift this
 * module exists to prevent.
 */
export const SHARE_ICONS = {
  facebook:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">'
    + '<path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>'
    + '</svg>',
  twitter:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">'
    + '<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>'
    + '</svg>',
  linkedin:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">'
    + '<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>'
    + '</svg>',
  whatsapp:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345m-5.446 7.443h-.016c-1.77 0-3.524-.48-5.055-1.38l-.36-.214-3.75.975 1.005-3.645-.239-.375a9.869 9.869 0 0 1-1.516-5.26c0-5.445 4.455-9.885 9.942-9.885 2.654 0 5.145 1.035 7.021 2.91 1.875 1.859 2.909 4.35 2.909 6.99-.004 5.444-4.46 9.885-9.935 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a12.062 12.062 0 0 0 5.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411"/></svg>',
  email:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>'
    + '<polyline points="22,6 12,13 2,6"/>'
    + '</svg>',
  sms:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>'
    + '</svg>',
  instagram:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>'
    + '<path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>'
    + '<line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>'
    + '</svg>',
  copy:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>',
}

/**
 * Whether the Web Share API (navigator.share) is available in this runtime.
 * SSR-safe: returns false when there is no navigator.
 */
export function canNativeShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

/**
 * Coarse desktop detection used to gate the SMS button (mobile-only).
 *
 * SSR-safe: returns true when `window` is undefined (so server-rendered markup
 * defaults to the desktop option set rather than emitting an SMS link that a
 * crawler/server context can't honor).
 *
 * Treats >= 768px viewport as desktop, AND — when matchMedia exists — requires
 * a fine pointer (mouse/trackpad). A wide touch device (large tablet) without
 * a fine pointer is still treated as mobile so it keeps its SMS button.
 */
export function isDesktop() {
  if (typeof window === 'undefined') return true
  const wideEnough = window.innerWidth >= 768
  const finePointer = typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: fine)').matches
    : true
  return wideEnough && finePointer
}

/**
 * Invoke the native OS share sheet.
 *
 * @returns {Promise<'shared'|'dismissed'|'unsupported'|'error'>}
 *   - 'shared'      the OS reported a successful share
 *   - 'dismissed'   the user aborted the share sheet (AbortError)
 *   - 'unsupported' navigator.share is not available
 *   - 'error'       navigator.share rejected for any other reason
 */
export async function nativeShare({ title, text, url }) {
  if (!canNativeShare()) return 'unsupported'
  try {
    await navigator.share({ title, text, url })
    return 'shared'
  } catch (err) {
    return err && err.name === 'AbortError' ? 'dismissed' : 'error'
  }
}

/**
 * Build the canonical, ordered share-option array.
 *
 * Order: facebook, twitter, linkedin, whatsapp, email, [sms only on mobile],
 * instagram, copy.
 *
 * @param {object}  args
 * @param {string}  args.url           Absolute campaign URL to share.
 * @param {string}  [args.campaignName] Display name; defaults to 'this campaign'.
 * @param {boolean} [args.desktop]     Override device detection (defaults to isDesktop()).
 * @returns {Array<{key,label,icon,url?,popup?,action?}>}
 */
export function buildShareOptions({ url, campaignName, desktop = isDesktop() }) {
  const name = campaignName || 'this campaign'
  const encodedUrl = encodeURIComponent(url)

  // Platform-tuned copy. Facebook + LinkedIn strip custom text per their API
  // policy (they scrape og: meta tags from the target page), so tuned text is
  // meaningful only on Twitter, WhatsApp, email, and SMS.
  const twitterText = encodeURIComponent(
    `Supporting ${name} 🏐 Every dollar funds breakthrough research for metastatic breast cancer patients. #DigPink #WeWontIgnoreStageIV`
  )
  const emailSubject = encodeURIComponent(`Will you join me in supporting ${name}?`)
  const emailBody = encodeURIComponent(
    `Hi,\n\n`
    + `I'm supporting ${name} and wanted to share it with you.\n\n`
    + `Side-Out Foundation funds breakthrough precision-medicine research for metastatic breast cancer — a disease that still has no cure. Every dollar raised through ${name} goes directly toward that research.\n\n`
    + `You can learn more and donate here: ${url}\n\n`
    + `Thanks for considering it.`
  )
  // SMS deep link — prefills the native Messages app on mobile.
  const smsBody = encodeURIComponent(
    `Supporting ${name} 🏐 — help fund metastatic breast cancer research. ${url}`
  )
  // WhatsApp shares a single text blob (link is embedded in the message).
  const whatsappText = encodeURIComponent(
    `Supporting ${name} — help fund metastatic breast cancer research. ${url}`
  )

  const options = [
    { key: 'facebook', label: 'Facebook',    icon: SHARE_ICONS.facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, popup: true },
    { key: 'twitter',  label: 'X / Twitter', icon: SHARE_ICONS.twitter,  url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${twitterText}`, popup: true },
    { key: 'linkedin', label: 'LinkedIn',    icon: SHARE_ICONS.linkedin, url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, popup: true },
    { key: 'whatsapp', label: 'WhatsApp',    icon: SHARE_ICONS.whatsapp, url: `https://wa.me/?text=${whatsappText}`, popup: true },
    { key: 'email',    label: 'Email',       icon: SHARE_ICONS.email,    url: `mailto:?subject=${emailSubject}&body=${emailBody}`, popup: false },
  ]

  // SMS is mobile-only: desktop browsers have no Messages app to hand off to.
  if (!desktop) {
    options.push({ key: 'sms', label: 'Text', icon: SHARE_ICONS.sms, url: `sms:?&body=${smsBody}`, popup: false })
  }

  // Instagram has no web share-intent URL that accepts a link, so its button
  // copies the link (for pasting into a story/DM) AND opens Instagram itself
  // (Rick 2026-06-11). Copy is the explicit "copy link" affordance.
  options.push({ key: 'instagram', label: 'Instagram', icon: SHARE_ICONS.instagram, action: 'copy-open', url: 'https://www.instagram.com/' })
  options.push({ key: 'copy',      label: 'Copy Link',  icon: SHARE_ICONS.copy,      action: 'copy' })

  return options
}

/**
 * Activate a single share option robustly.
 *
 * @param {object} opt           A single option from buildShareOptions().
 * @param {object} [handlers]
 * @param {Function} [handlers.onCopy] Called for copy-actions and as the
 *   fallback when a popup is blocked by the browser.
 * @returns {'copy'|'copy-open'|'navigate'|'popup'|'popup-blocked-fallback'}
 */
export function openShareOption(opt, { onCopy } = {}) {
  // Copy-actions (copy): never navigate, just copy the link.
  if (opt.action === 'copy') {
    if (typeof onCopy === 'function') onCopy()
    return 'copy'
  }

  // Copy-then-open (instagram): copy the link so it's ready to paste, then
  // open Instagram in a new tab. Copy comes FIRST — the clipboard API needs
  // this document focused, and window.open may steal focus; both run in the
  // same user-gesture tick so neither is blocked. A new tab (not the 600x500
  // intent popup) because instagram.com is a full app, and on mobile the
  // top-level navigation lets the OS universal/app link open the native app.
  if (opt.action === 'copy-open') {
    if (typeof onCopy === 'function') onCopy()
    const win = window.open(opt.url, '_blank')
    if (win) {
      // Same reverse-tabnabbing protection as the popup path below.
      try { win.opener = null } catch (e) { /* cross-origin handle */ }
      return 'copy-open'
    }
    // Tab blocked: the link is already copied, so the button still did
    // something useful rather than dead-ending.
    return 'copy'
  }

  // Non-popup links (mailto:, sms:) navigate the current document — these
  // hand off to the OS mail/messages handler rather than opening a tab.
  if (!opt.popup) {
    window.location.href = opt.url
    return 'navigate'
  }

  // Popup share intents (FB, X, LinkedIn, WhatsApp): open a centered window.
  const w = 600
  const h = 500
  const left = Math.max(0, (window.outerWidth - w) / 2 + (window.screenX || 0))
  const top = Math.max(0, (window.outerHeight - h) / 2 + (window.screenY || 0))
  // Deliberately NO `noopener`/`noreferrer` in the feature string: per MDN,
  // either token forces window.open() to return `null` even on a SUCCESSFUL
  // open, which would defeat the block-detection below (every real share would
  // be misread as blocked and double-opened). We preserve the security intent
  // by nulling `opener` on the returned handle instead. Also no non-standard
  // `popup=yes` token — some browsers treat an unknown feature as a signal to
  // ignore the rest of the string.
  const features = `width=${w},height=${h},left=${left},top=${top}`

  const win = window.open(opt.url, '_blank', features)
  if (win) {
    // Sever the new window's back-reference to this document (reverse tabnabbing
    // protection) without relying on the feature-string flags that null the return.
    try { win.opener = null } catch (e) { /* cross-origin handle: opener already inaccessible */ }
    return 'popup'
  }

  // Popup genuinely blocked (window.open returned falsy): copy the link so the
  // share is never a dead end.
  if (typeof onCopy === 'function') onCopy()
  return 'popup-blocked-fallback'
}

/**
 * Query flags that open a modal on arrival (`?register=1` → signup wizard,
 * `?support=1` → Support Hub). They describe the *arriving* visitor's intent
 * and must never ride along on a link that visitor then shares.
 */
export const MODAL_INTENT_PARAMS = ['register', 'support']

/**
 * The campaign URL a visitor should hand to someone else: the current page
 * with every modal-intent flag removed. Dedicated registration links use a
 * separate URL. Other query state (utm_*, pledge-id) and the hash are preserved.
 * Never mutates `window.location`.
 *
 * @param {string} [href] Absolute URL to clean; defaults to the current page.
 * @returns {string} '' when there is no window and no href was supplied.
 */
export function cleanCampaignShareUrl(href) {
  const source = href || (typeof window !== 'undefined' ? window.location?.href : '')
  if (!source) return ''
  try {
    const url = new URL(source)
    MODAL_INTENT_PARAMS.forEach(param => url.searchParams.delete(param))
    return url.href
  } catch (_e) {
    return source
  }
}

/**
 * Remove one modal-intent flag from the address bar without adding a history
 * entry — used when the visitor closes the modal the flag opened. Leaves every
 * other query parameter, the hash, and `history.state` untouched.
 *
 * @param {string} param One of MODAL_INTENT_PARAMS.
 * @returns {boolean} true when the flag was present and removed.
 */
export function clearModalIntentParam(param) {
  if (typeof window === 'undefined' || !window.history?.replaceState) return false
  const url = new URL(window.location.href)
  if (!url.searchParams.has(param)) return false
  url.searchParams.delete(param)
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  return true
}

/**
 * Copy `text` to the clipboard. Prefers the async Clipboard API and falls back
 * to selecting `inputEl` + execCommand for older browsers / insecure contexts.
 *
 * @returns {Promise<boolean>} true on success. On failure the input (when
 *   given) is left selected so the visitor can copy manually.
 */
export async function copyTextToClipboard(text, inputEl) {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (_e) {
    // Some embedded browsers expose the API but deny access. Try the
    // selected-input path before falling back to manual copying.
  }
  if (inputEl) {
    inputEl.focus?.({ preventScroll: true })
    inputEl.select()
    inputEl.setSelectionRange(0, text.length)
    try {
      if (typeof document.execCommand === 'function' && document.execCommand('copy')) return true
    } catch (_e) {
      // Leave the address selected for manual copying.
    }
  }
  return false
}
/** Only intercept normal link activation; preserve new-tab/window gestures. */
export function isUnmodifiedPrimaryClick(event) {
  return !event || (!event.defaultPrevented
    && (event.button === undefined || event.button === 0)
    && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey)
}
